import { useState, useEffect } from "react";
import { Form, useActionData, useNavigation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import { useAuth } from "../../hooks/useAuth";
import type { ActionResult } from "../../routes/actions";
import { generateSlug } from "../../utils";
import styles from "./CreateOrganizationPage.module.css";

export default function CreateOrganizationPage() {
  const { t } = useTranslation();
  const { refetchUser } = useAuth();
  const navigation = useNavigation();
  const actionData = useActionData() as ActionResult | undefined;
  const isSubmitting = navigation.state === "submitting";

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);

  useEffect(() => {
    if (autoGenerateSlug && name) {
      setSlug(generateSlug(name));
    }
  }, [name, autoGenerateSlug]);

  useEffect(() => {
    if (!actionData) return;

    if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  useEffect(() => {
    if (navigation.state === "loading" && actionData === undefined) {
      refetchUser();
    }
  }, [navigation.state, actionData, refetchUser]);

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>{t("organizations.create.title")}</h1>
            <p>{t("organizations.create.subtitle")}</p>
          </div>

          <Form method="post" className={styles.form}>
            <div
              className={`${styles.formGroup} ${actionData?.fieldErrors?.name ? styles.formGroupError : ""}`}
            >
              <label htmlFor="name">{t("organizations.create.nameLabel")}</label>
              <input
                id="name"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("organizations.create.namePlaceholder")}
                disabled={isSubmitting}
              />
              {actionData?.fieldErrors?.name && (
                <span className={styles.formError}>{actionData.fieldErrors.name}</span>
              )}
            </div>

            <div
              className={`${styles.formGroup} ${actionData?.fieldErrors?.slug ? styles.formGroupError : ""}`}
            >
              <label htmlFor="slug">
                {t("organizations.create.slugLabel")}
                <button
                  type="button"
                  className={styles.toggleButton}
                  onClick={() => setAutoGenerateSlug(!autoGenerateSlug)}
                >
                  {autoGenerateSlug
                    ? t("organizations.create.slugAuto")
                    : t("organizations.create.slugCustom")}
                </button>
              </label>
              <input
                id="slug"
                name="slug"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={t("organizations.create.slugPlaceholder")}
                disabled={isSubmitting || autoGenerateSlug}
                className={autoGenerateSlug ? styles.disabledInput : ""}
              />
              {actionData?.fieldErrors?.slug && (
                <span className={styles.formError}>{actionData.fieldErrors.slug}</span>
              )}
              <small className={styles.helpText}>{t("organizations.create.slugHelp")}</small>
            </div>

            <div className={styles.formActions}>
              <LinkButton to="/organizations" variant="secondary">
                {t("common.cancel")}
              </LinkButton>
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                loadingText={t("organizations.create.submitting")}
              >
                {t("organizations.create.submitButton")}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
}
